/**
 * offlineQueue — Cola de mutaciones para resiliencia en red débil.
 *
 * Persistencia: IndexedDB vía idb-keyval. Las mutaciones encoladas sobreviven
 * a recargas y a cierre del browser.
 *
 * Uso:
 *   queueMutation({ url, method, body, label });   // encola
 *   await flushQueue();                             // intenta sincronizar
 *   onQueueChange(cb);                              // suscripción a cambios
 *
 * El flush también se dispara automáticamente al detectar 'online' event.
 */
import { get, set } from 'idb-keyval';

const KEY = 'nara-muelle-offline-queue';
const listeners = new Set();

let inFlightFlush = false;

function notify() {
  listeners.forEach(cb => { try { cb(); } catch {} });
}

export function onQueueChange(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export async function getQueue() {
  return (await get(KEY)) || [];
}

export async function queueLength() {
  return (await getQueue()).length;
}

export async function queueMutation({ url, method = 'POST', body, label, headers }) {
  const q = await getQueue();
  q.push({
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    url,
    method,
    body: body || null,
    label: label || `${method} ${url}`,
    headers: headers || null,
    createdAt: Date.now(),
    attempts: 0,
  });
  await set(KEY, q);
  notify();
  // Intentar flush si hay conexión
  if (typeof navigator !== 'undefined' && navigator.onLine) {
    flushQueue().catch(() => {});
  }
}

function getAuthHeaders(extra = {}) {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
}

async function executeOne(op) {
  const res = await fetch(op.url, {
    method: op.method,
    headers: getAuthHeaders(op.headers || {}),
    body: op.body ? JSON.stringify(op.body) : undefined,
  });
  if (!res.ok && res.status >= 500) {
    // 5xx → retry; 4xx → descartar (request inválida)
    throw new Error(`HTTP ${res.status}`);
  }
  return res;
}

export async function flushQueue() {
  if (inFlightFlush) return;
  inFlightFlush = true;
  try {
    let q = await getQueue();
    while (q.length > 0) {
      const op = q[0];
      try {
        await executeOne(op);
        q = q.slice(1);
        await set(KEY, q);
        notify();
      } catch (e) {
        // Falló — incrementar attempts y dejar el resto en cola
        q[0] = { ...op, attempts: (op.attempts || 0) + 1, lastError: String(e) };
        await set(KEY, q);
        notify();
        // Si lleva muchos intentos, descartar para no bloquear cola
        if (q[0].attempts >= 5) {
          q = q.slice(1);
          await set(KEY, q);
          notify();
          console.warn('[offlineQueue] descartando operación tras 5 intentos:', op);
          continue;
        }
        break; // Salir para reintentar más tarde
      }
    }
  } finally {
    inFlightFlush = false;
  }
}

// Auto-flush al recuperar conexión
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => { flushQueue().catch(() => {}); });
  // Intentar al cargar (por si hay cola pendiente del cierre anterior)
  setTimeout(() => { flushQueue().catch(() => {}); }, 2000);
}

export function isOnline() {
  return typeof navigator !== 'undefined' ? navigator.onLine : true;
}
